import { GameState } from '../objects/GameState';
import { TiledGameObject } from 'src/games/shared/phaserHelpers/HandyTypes';
import { Color } from 'src/games/shared/ColorValue';
import { Helpers } from '../objects/Helpers';
import initializeInteractiveCursor from 'src/games/shared/InteractiveCursorUtil';
import TranslateApi from 'src/utils/TranslateHelper';

export class ColorChangerScene extends Phaser.Scene {
    gameWidth: number;
    gameHeight: number;
    background: Phaser.GameObjects.Image;
    map: Phaser.Tilemaps.Tilemap;
    currColor: number;
    beaker: Phaser.GameObjects.Image;
    eyedropper: Phaser.GameObjects.Image;
    beakerLiquid: Phaser.GameObjects.Image;
    eyedropperLiquid: Phaser.GameObjects.Image;

    constructor() {
        super({
            key: "ColorChangerScene_" + Math.random(),
        });
    }

    preload(): void {
        // All assets were loaded in MainScene
        this.load.tilemapTiledJSON("color_changer_scene_map", require("/src/assets/qci/maps/labDiagnosisGame/lab_diagnosis_color_changer_scene.json"));
    }

    create(): void {
        this.initialize();
        this.generateScene();
        initializeInteractiveCursor(this);
    }

    initialize() {
        this.gameWidth = this.textures.game.config.width as number;
        this.gameHeight = this.textures.game.config.height as number;
        this.map = this.make.tilemap({ key: 'color_changer_scene_map' });
    }

    generateScene() {
        let background = this.add.image(this.gameWidth / 2, this.gameHeight / 2, "background");
        background.setDisplaySize(this.gameWidth, this.gameHeight);
        background.setOrigin(0.5);
        background.setDepth(0);

        this.addObjects();
        Helpers.addBackButton(this);
    }

    addObjects(): void {
        var translateString = "Diagnosis.Phaser.ColorChangerScene.";
        this.map.getObjectLayer("Scene").objects.forEach((ob: TiledGameObject) => {
            if (ob.name === "beaker") {
                this.beaker = this.add.image(ob.x, ob.y, "assets", "beaker_empty");
                this.beaker.setOrigin(0);
                this.beaker.setDisplaySize(ob.width, ob.height);

                this.beakerLiquid = this.add.image(ob.x, ob.y, "assets", "beaker_liquid");
                this.beakerLiquid.setOrigin(0);
                this.beakerLiquid.setDepth(1);
                this.beakerLiquid.setDisplaySize(ob.width, ob.height);
                this.beakerLiquid.setTint(Color.COLORS.get('light grey'));
            }
            else if (ob.name === "eyedropper") {
                this.eyedropper = this.add.image(ob.x, ob.y, "assets", "eyedropper_empty");
                this.eyedropper.setOrigin(0);
                this.eyedropper.setDisplaySize(ob.width, ob.height);
                this.eyedropper.setInteractive();
                this.eyedropper.setRotation(0.785); // 45 degrees (in radians)
                this.eyedropper.on('pointerdown', () => {
                    this.eyedropper.setAlpha(1);
                    this.eyedropperLiquid.setAlpha(1);
                    this.tweens.killTweensOf(this.eyedropper);
                    this.tweens.killTweensOf(this.eyedropperLiquid);
                    this.eyedropper.disableInteractive();
                    this.dropBloodIntoBeaker();
                });

                this.eyedropperLiquid = this.add.image(ob.x, ob.y, "assets", "eyedropper_liquid");
                this.eyedropperLiquid.setOrigin(0);
                this.eyedropperLiquid.setDisplaySize(ob.width, ob.height);
                this.eyedropperLiquid.setRotation(0.785); // 45 degrees (in radians)
                this.eyedropperLiquid.setTint(Color.COLORS.get('alien_blood'));

                Helpers.flashObject(this, this.eyedropper);
                Helpers.flashObject(this, this.eyedropperLiquid);
            }
            else if (ob.name === "text") {
                var title_fontStyle = { fontFamily: 'Russo One, Roboto Mono, Arial', fontSize: 20, color: '#ffffff', padding: 20, backgroundColor: "#000000" };
                var text = TranslateApi.translate(translateString+"text");
                var box =  this.add.text(ob.x, ob.y, text, title_fontStyle).setWordWrapWidth(ob.width, true).setDepth(1);
                box.setX(this.gameWidth/2 - box.displayWidth/2);
                this.add.image(box.x - 5, box.y - 5, "pixel").setDisplaySize(box.displayWidth + 10, box.displayHeight + 10).setDepth(.5).setOrigin(0).setTint(0x0033cc);
            }
        });
    }

    /**
     * Simulates drop of "blood" coming out of eyedropper into beaker
    */
    dropBloodIntoBeaker(): void {
        this.map.getObjectLayer("Animation").objects.forEach((ob: TiledGameObject) => {
            if (ob.name === "drop") {
                var drop = this.add.image(ob.x, ob.y, "assets", "droplet");
                drop.setOrigin(0);
                drop.setTint(Color.COLORS.get("alien_blood"));
                drop.setDisplaySize(ob.width, ob.height);

                // Tween to empty eyedropper liquid (shrinks height)
                this.tweens.add({
                    targets: this.eyedropperLiquid,
                    duration: 4000,
                    x: this.eyedropper.getBottomLeft().x + 0.5,
                    y: drop.y - 1,
                    ease: "Power2",
                    displayHeight: {
                        value: 0,
                    },
                });

                // Tween to empty eyedropper liquid (shrink when get to the bottom of dropper)
                this.tweens.add({
                    targets: this.eyedropperLiquid,
                    duration: 2000,
                    delay: 1750,
                    x: drop.x,
                    y: drop.y,
                    ease: "Power2",
                    scaleX: {
                        value: 0.2,
                    },
                });

                // Tween to drop 3 droplets of blood from eyedropper into beaker, then change liquid color
                this.tweens.add({
                    delay: 100,
                    targets: drop,
                    repeat: 2,
                    y: {
                        value: 600,
                        duration: 1200,
                        ease: 'Power2'
                    },
                    alpha: {
                        value: 0,
                        duration: 1000,
                    },
                    onComplete: () => {
                        // Simulate liquid in beaker changing to new color
                        Helpers.fadeOut(this, this.beakerLiquid, 2500, 0.3, 1);

                        this.currColor = GameState.current.correctDiagnosis.colorChangerColor;
                        this.beakerLiquid.setTint(this.currColor);

                        // Test is over, now can let them move on to the next one
                        Helpers.addNextButton(this);
                    }
                });
            }
        });
    }
}



// WEBPACK FOOTER //
// ./src/games/DiagnosisGame/scenes/ColorChangerScene.ts